import { createSelector } from "reselect"
import { chain, findIndex, get, isFinite, mapValues, forEach, keys, some, every, isNumber, isEmpty, isObject } from "lodash"
import { numberFormatter } from "helpers/formatters/numbers"
import { getContentSets } from "selectors/securities-table"
import { getFactorName } from "helpers/factors"
import { CONTENT_SET_DATA_TYPES } from "helpers/contentSetConstants"
import { getModelFactors } from "selectors/modelMetadata"

function notLessThanMin(min) {
  return notBetweenMinMax(min, Infinity)
}

function notBetweenMinMax(min, max) {
  return (value) => {
    const numberValue = parseFloat(value)
    return some([
      (v) => !isNumber(v),
      (v) => parseFloat(v) < min,
      (v) => parseFloat(v) > max,
    ], fn => fn(numberValue))
  }
}

export const CONSTRAINTS = {
  tradeSwaps: () => ({
    label: "Trades swaps",
    defaultValue: true,
    isBoolean: true,
    tooltip: "This allows baskets to be treated as a security that can be traded, if any are held in this portfolio",
  }),
  maxTurnover: () => ({
    label: "Max turnover",
    defaultValue: 20,
    unit: "%",
    min: 0,
    tooltip: "Restrict the total percent equity of the portfolio that can be traded. Defaults to 100",
    validation: value => notLessThanMin(0)(value) ? "% Max turnover must be greater than or equal to 0" : false,
  }),
  maxConcentration: () => ({
    label: "Max concentration",
    unit: "%",
    defaultValue: 0,
    min: 0,
    max: 100,
    tooltip: "The maximum % of equity that any single security may hold",
    validation: value => notBetweenMinMax(0, 100)(value) ? "% Max concentration must be between 0 and 100" : false,
  }),
  maxTrade: () => ({
    label: "Max trade size",
    defaultValue: { percentADV: 25, percentEquity: 25, percentOriginalEconomicExposure: 25 },
    unit: { percentADV: "% ADV", percentEquity: "% Eq", percentOriginalEconomicExposure: "% Rel" },
    tooltip: {
      percentADV: "Maximum per security trade size as a percentage of its average daily volume. Defaults to 500 (5 days)",
      percentEquity: "Maximum per security trade size as a percentage of equity",
      percentOriginalEconomicExposure: "Maximum per security trade size as a percentage relative to the security's initial economic exposure.",
    },
    min: 0,
    validation: (value, unit = "") => value && value < 0 ? `${unit} must be positive` : false,
  }),
  equityChange: () => ({
    label: "Equity change",
    defaultValue: 3,
    unit: "% GMV",
    tooltip: "Governs the amount that the optimizer is allowed to change the portfolio's total NAV",
  }),
  netLong: ({ netLong }) => ({
    label: "Net long",
    defaultValue: numberFormatter(netLong * 100),
    unit: "% GMV",
    helperText: "Long",
    min: 0,
    max: 100,
    tooltip: "The ratio between the portfolio's long book and short book, where 50% net long results in a market neutral portfolio.",
    secondHelperText: "Short",
    secondField: value => {
      const floatValue = parseFloat(value)

      if (isFinite(floatValue)) {
        return numberFormatter(100 - floatValue)
      } else {
        return "-"
      }
    },
    disabled: ({ portfolioType }) => portfolioType !== "LONG_SHORT",
    validation: value => value && (value < 0 || value > 100) ? "Net long must be 0-100" : false,
  }),
}

const SETTINGS = {
  forecast: () => ({
    id: "forecast",
  }),
  horizon: () => ({
    id: "horizon",
  }),
}

function compareObjects(o1, o2) {
  for (const p in o1) {
    if (Object.prototype.hasOwnProperty.call(o1, p)) {
      if (o1[p] !== o2[p]) {
        return false
      }
    }
  }
  for (const p in o2) {
    if (Object.prototype.hasOwnProperty.call(o2, p)) {
      if (o1[p] !== o2[p]) {
        return false
      }
    }
  }
  return true
}

function computeMinMaxExposure(min, max) {
  const error = max < min ? "Max should be >= Min" : ""

  if (error) {
    min = null
    max = null
  }

  return { min, max, error }
}

function getIsFactorCurrency(contentSetId, contentSets) {
  const contentSetDataType = chain(contentSets)
    .find(({ id }) => contentSetId === id)
    .get("dataType")
    .value()

  return contentSetDataType === CONTENT_SET_DATA_TYPES.SENSITIVITY
}

function getFactorExposure(factorId, contentSetId, contentSets, portfolioData) {
  const nodeId = contentSetId ? chain(contentSets)
    .find(({ id }) => contentSetId === id)
    .get("escapedId")
    .value() : "exposure"

  if (nodeId) {
    return chain(portfolioData)
      .get([nodeId, 0, "factors"])
      .find(({ id }) => id === factorId)
      .get("value", 0)
      .value()
  }
}

const EMPTY_ARRAY = []

export const getSelectedConstraints = (state, props) => get(props, "activatedConstraints.general")
export const getSelectedSettings = (state, props) => get(props, "activatedConstraints.settings")
const getCurrentConstraints = (state, props) => get(props, "constraints.general")
const getCurrentSettings = (state, props) => get(props, "constraints.settings")
const getCurrentExposure = (state, props) => get(props, "constraints.exposure", EMPTY_ARRAY)
const getPortfolioData = (state, props) => get(props, "portfolioData")
export const getExposure = (state, props) => get(props, "activatedConstraints.exposure")
const getPortfolioNetLong = (state, props) => get(props, "portfolioNetLong")

export const getUIConstraints = createSelector(
  [getSelectedConstraints, getCurrentConstraints, getPortfolioNetLong],
  (selected, currentConstraints, netLong) => mapValues(CONSTRAINTS, (item, id) => {
    const constraint = item({ netLong })
    let error = false

    if (constraint.validation) {
      error = !selected[id] ? constraint.validation(currentConstraints[id]) : constraint.validation(selected[id].value)
    }

    return {
      selected: !selected[id] ? currentConstraints[id] !== undefined : selected[id].value !== null,
      value: !selected[id] ? currentConstraints[id] : selected[id].value,
      error,
      ...constraint,
    }
  }),
)

export const getSettings = createSelector(
  [getSelectedSettings, getCurrentSettings],
  (selected, currentSettings) =>
    mapValues(SETTINGS, (item, id) =>
      ({ value: !selected[id] ? currentSettings[id] : selected[id].value, ...item() }),
    ),
)

export const getNumberOfCurrentConstraints = createSelector(
  [getCurrentConstraints],
  constraints => get(keys(constraints), "length"),
)

export const getComputedConstraints = createSelector(
  [getUIConstraints],
  (selectedConstraints) =>
    chain(selectedConstraints)
      .mapValues(({ value }) => value)
      .pickBy(value => value)
      .value(),
)

export const getComputedSettings = createSelector(
  [getSettings],
  (settings) =>
    chain(settings)
      .mapValues(({ value, toggle }) => toggle ? !!value : value)
      .pickBy((value, id) => value || settings[id].toggle)
      .value(),
)

export const getApplyButtonEnabled = createSelector(
  [getComputedConstraints, getCurrentConstraints],
  (selectedConstraints, currentConstraints) => !compareObjects(selectedConstraints, currentConstraints),
)

export const getApplySettingsButtonEnabled = createSelector(
  [getComputedSettings, getCurrentSettings],
  (selectedSettings, currentSettings) => !compareObjects(selectedSettings, currentSettings),
)

export const getFactors = createSelector(
  [getExposure, getCurrentExposure, getModelFactors, getContentSets, getPortfolioData],
  (exposure, currentExposure, factorsMetadata, contentSets, portfolioData) => {
    const computedCurrentExposure = currentExposure.map(factor => {
      let min = factor.min
      let max = factor.max
      let error = false

      const id = factor.contentSetId ? `${factor.contentSetId}.${factor.id}` : factor.id
      if (exposure[id]) {
        const computedMinMaxExposure = computeMinMaxExposure(exposure[id].min, exposure[id].max)
        min = computedMinMaxExposure.min
        max = computedMinMaxExposure.max
        error = computedMinMaxExposure.error
      }

      return {
        id: factor.id,
        contentSetId: factor.contentSetId,
        originalMin: factor.min,
        originalMax: factor.max,
        error,
        min,
        max,
        name: getFactorName(factor.id, factor.contentSetId, factorsMetadata, contentSets),
        isCurrency: getIsFactorCurrency(factor.contentSetId, contentSets),
        zScore: getFactorExposure(factor.id, factor.contentSetId, contentSets, portfolioData),
      }
    })

    const computedExposure = chain(exposure)
      .map((target) => {
        if (findIndex(computedCurrentExposure, { id: target.factorId, contentSetId: target.contentSetId }) >= 0) return null
        const { min, max, error } = computeMinMaxExposure(target.min, target.max)

        return ({
          id: target.factorId,
          contentSetId: target.contentSetId,
          error,
          min,
          max,
          name: getFactorName(target.factorId, target.contentSetId, factorsMetadata, contentSets),
          isCurrency: getIsFactorCurrency(target.contentSetId, contentSets),
          zScore: getFactorExposure(target.factorId, target.contentSetId, contentSets, portfolioData),
        })
      })
      .filter(factor => factor)
      .value()

    return [...computedCurrentExposure, ...computedExposure]
  },
)

export const getNumberOfCurrentExposure = createSelector(
  [getCurrentExposure],
  currentExposure => currentExposure.length,
)

export const getFactorsErrors = createSelector(
  [getFactors],
  factors => some(factors, factor => factor.error),
)

export const getConstraintsErrors = createSelector(
  [getUIConstraints],
  constraints => chain(constraints)
    .filter(c => c.selected)
    .some(constraint => {
      const { value, validation, error } = constraint
      if (!isObject(value)) {
        return error
      }
      // if the value prop is an object, it contains multiple values
      // check if some of the validaitons fail or if every value is empty
      return some(value, v => validation(v)) || every(value, v => !isNumber(v) && isEmpty(v))
    })
    .value(),
)

export const getApplyExposureButtonEnabled = createSelector(
  [getFactors],
  (factors) => {
    if (factors.length > 0) {
      let disabled = true

      forEach(factors, target => {
        if (!isFinite(target.min) || !isFinite(target.max)) {
          disabled = true
          return false
        } else if (disabled
          && (isFinite(target.min) && isFinite(target.max))
          && (target.min !== target.originalMin || target.max !== target.originalMax)
        ) {
          disabled = false
        }
      })

      return !disabled
    } else {
      return false
    }
  },
)
